import type { WSTickerSnapshot } from "@ekidenfi/ts-sdk";
import { makeAutoObservable, runInAction } from "mobx";

import type { RootStore } from "@stores/RootStore";

import { APTOS_COIN_INTERNAL } from "@constants";
import { DEFAULT_MARKET_CONFIG, MARKET_CONFIG, MARKET_SORT_ORDER, type MarketParams } from "@constants/markets";
import { TOKEN_METADATA, TOKEN_METADATA_BY_SYMBOL, getBaseSymbolFromMarket } from "@constants/metadata";
import { Address } from "@utils/Address";
import { BN } from "@utils/BN";
import { IntervalUpdater } from "@utils/IntervalUpdater";

import type { Market } from "@src/api";
import type { TokenMetadata } from "@src/api/aptos";
import type { InstrumentInfo, Leverage } from "@src/api/types";
import { type MarketData, marketService } from "@src/services";

import type { TokenWithoutAmount } from "./BalanceStore";

const FAVORITE_MARKETS_KEY = "ekiden_favorite_markets";

const getSymbol = (symbol: string) => {
  let cleanSymbol = symbol.split("-").slice(0, 1).join("");
  if (cleanSymbol.startsWith("W")) {
    cleanSymbol = cleanSymbol.substring(1);
  }
  return cleanSymbol;
};

// Throttle interval for ticker updates (ms)
const TICKER_THROTTLE_MS = 100;
// Maximum entries in marketData map (active + favorites + buffer)
const MAX_MARKET_DATA_ENTRIES = 50;

export class MarketStore {
  private marketData: Map<string, MarketData | null> = new Map();
  private allTokensMetadata: Map<string, TokenMetadata | null> = new Map();
  private leverages: Map<string, number> = new Map();
  private instrumentsInfo: Map<string, InstrumentInfo> = new Map();
  private marketDataIntervalUpdater: IntervalUpdater;

  // Tracks leverage change requests awaiting WS confirmation.
  // `active` becomes true once the REST call returns, so WS updates
  // that arrive while the call is in-flight are ignored.
  private pendingLeverageChanges: Map<string, { requestedLeverage: number; active: boolean }> = new Map();

  // Ticker throttling
  private tickerBuffer: Map<string, WSTickerSnapshot> = new Map();
  private tickerFlushScheduled = false;

  // Index for fast market lookup by symbol
  private marketIndexBySymbol: Map<string, number> = new Map();

  requiredMarketId: string | null = null;
  activeMarket: Market | null = null;
  allMarkets: Market[] = [];
  latestFillPrice: BN | null = null;
  favoriteMarketAddresses: string[] = [];

  // === STABLE FIELDS (only update on market change, not on tickers) ===
  private _stableActiveMarketAddr: string | null = null;
  private _stableActiveMarketSymbol: string | null = null;
  private _stableActiveMarketQuoteDecimals = 0;
  private _stableActiveMarketBaseDecimals = 0;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
    this.loadFavoritesFromStorage();
    this.marketDataIntervalUpdater = new IntervalUpdater(async () => {
      if (!this.activeMarket) return;
      await this.loadMarketStats(this.activeMarket);
    }, 60 * 1000);
    this.marketDataIntervalUpdater.run();
  }

  get favoriteMarkets(): Market[] {
    return this.favoriteMarketAddresses
      .map((addr) => this.allMarkets.find((m) => m.addr === addr))
      .filter((m): m is Market => m !== undefined);
  }

  // === STABLE COMPUTED GETTERS (don't change on ticker updates) ===
  get activeMarketAddr(): string | null {
    return this._stableActiveMarketAddr;
  }

  get activeMarketSymbol(): string | null {
    return this._stableActiveMarketSymbol;
  }

  get activeMarketQuoteDecimals(): number {
    return this._stableActiveMarketQuoteDecimals;
  }

  get activeMarketBaseDecimals(): number {
    return this._stableActiveMarketBaseDecimals;
  }

  get isActiveMarketReady(): boolean {
    return this._stableActiveMarketAddr !== null;
  }

  get tokens(): TokenWithoutAmount[] {
    const uniqueTokens = new Map<string, TokenWithoutAmount>();

    for (const market of this.allMarkets) {
      const baseSymbol = getBaseSymbolFromMarket(market.symbol);
      const baseMeta = TOKEN_METADATA_BY_SYMBOL[baseSymbol];
      const quoteMeta = TOKEN_METADATA[market.quote_addr];

      if (!baseMeta || !quoteMeta) {
        continue;
      }

      if (!uniqueTokens.has(baseSymbol)) {
        uniqueTokens.set(baseSymbol, {
          ...baseMeta,
          address: baseSymbol,
          decimals: market.base_decimals,
        });
      }

      if (!uniqueTokens.has(market.quote_addr)) {
        uniqueTokens.set(market.quote_addr, {
          ...quoteMeta,
          address: market.quote_addr,
          decimals: market.quote_decimals,
        });
      }
    }

    const aptosMeta = TOKEN_METADATA[APTOS_COIN_INTERNAL];
    if (aptosMeta && !uniqueTokens.has(APTOS_COIN_INTERNAL)) {
      uniqueTokens.set(APTOS_COIN_INTERNAL, {
        ...aptosMeta,
        address: APTOS_COIN_INTERNAL,
        decimals: 8,
      });
    }

    return Array.from(uniqueTokens.values());
  }

  get marketPrice() {
    if (!this.activeMarket) return BN.ZERO;

    // Prefer last traded price, but fall back to mark/oracle so the UI can
    // compute quote↔base conversions even when there were no recent trades
    // (common in local/dev environments).
    const last = Number(this.activeMarket.last_price) || 0;
    if (last > 0) return new BN(last);

    const mark = Number(this.activeMarket.mark_price) || 0;
    if (mark > 0) return new BN(mark);

    const oracle = Number(this.activeMarket.oracle_price) || 0;
    if (oracle > 0) return new BN(oracle);

    return BN.ZERO;
  }

  get baseToken(): TokenWithoutAmount | null {
    if (!this.activeMarket) return null;

    const baseSymbol = getBaseSymbolFromMarket(this.activeMarket.symbol);
    const meta = TOKEN_METADATA_BY_SYMBOL[baseSymbol];

    if (!meta) return null;

    return {
      ...meta,
      address: baseSymbol,
      decimals: this.activeMarket.base_decimals,
    };
  }

  // Stable version that uses cached symbol (doesn't trigger on ticker updates)
  get baseTokenSymbol(): string | null {
    if (!this._stableActiveMarketSymbol) return null;
    const baseSymbol = getBaseSymbolFromMarket(this._stableActiveMarketSymbol);
    const meta = TOKEN_METADATA_BY_SYMBOL[baseSymbol];
    return meta?.symbol ?? null;
  }

  get quoteToken(): TokenWithoutAmount | null {
    if (!this.activeMarket) return null;

    const quoteAssetAddr = this.rootStore.configStore.quoteAssetMetadata;
    const address = Address.from(quoteAssetAddr).toString();
    const meta = TOKEN_METADATA[quoteAssetAddr] || TOKEN_METADATA[address];

    if (!meta) return null;

    return {
      ...meta,
      address: quoteAssetAddr,
      decimals: this.activeMarket.quote_decimals,
    };
  }

  get currentLeverage(): number {
    if (!this.activeMarket) return 1;
    const leverage = this.leverages.get(this.activeMarket.addr);
    return leverage ?? Number(this.activeMarket.max_leverage);
  }

  getMarketData = (market: string): Required<MarketData> => {
    const defaultValue: Required<MarketData> = {
      fundingRate: 0,
      nextFundingTime: new Date(),
      oraclePrice: 0,
      volume24h: 0,
      change24h: 0,
      high24h: 0,
      low24h: 0,
    };

    const data = this.marketData.get(market);

    if (!data) {
      return defaultValue;
    }

    return {
      fundingRate: data.fundingRate ?? defaultValue.fundingRate,
      nextFundingTime: data.nextFundingTime ?? defaultValue.nextFundingTime,
      oraclePrice: data.oraclePrice ?? defaultValue.oraclePrice,
      volume24h: data.volume24h ?? defaultValue.volume24h,
      change24h: data.change24h ?? defaultValue.change24h,
      high24h: data.high24h ?? defaultValue.high24h,
      low24h: data.low24h ?? defaultValue.low24h,
    };
  };

  getMarketConfig = (market: string): MarketParams => {
    const staticConfig = MARKET_CONFIG[market] ?? DEFAULT_MARKET_CONFIG;
    const marketData = this.allMarkets.find((m) => m.addr === market);

    if (!marketData) {
      return staticConfig;
    }

    const instrumentInfo = this.instrumentsInfo.get(marketData.symbol);
    if (!instrumentInfo) {
      return staticConfig;
    }

    const dynamicPrecision = this.generatePrecisionOptions(
      instrumentInfo.price_filter.tick_size,
      String(marketData.last_price),
    );

    return {
      ...staticConfig,
      precision: dynamicPrecision,
    };
  };

  /**
   * Generate orderbook precision options based on tick_size and current price.
   * Similar to Bybit's approach: generate "nice" round numbers that make sense
   * for the current price level (roughly 0.0001% to 0.5% of price).
   */
  private generatePrecisionOptions = (
    tickSizeStr: string,
    lastPriceStr: string,
  ): { value: string; label: string }[] => {
    const tickSize = Number.parseFloat(tickSizeStr);
    const lastPrice = Number.parseFloat(lastPriceStr);

    if (!tickSize || tickSize <= 0 || !lastPrice || lastPrice <= 0) {
      return DEFAULT_MARKET_CONFIG.precision;
    }

    // Generate "nice" precision values based on price magnitude
    // For BTC ($96000): 100, 50, 10, 5, 1, 0.5, 0.1
    // For APT ($10): 0.1, 0.05, 0.01, 0.005, 0.001
    const options: { value: string; label: string }[] = [];

    // Calculate target range based on price
    const minPrecision = Math.max(tickSize, lastPrice * 0.000001); // At least tick_size or 0.0001% of price
    const maxPrecision = lastPrice * 0.002; // Up to 0.2% of price

    // Generate nice round numbers: 1, 2, 5, 10, 20, 50, 100, etc.
    // and 0.1, 0.2, 0.5, 0.01, 0.02, 0.05, etc.
    const baseValues = [1, 2, 5];
    const allPrecisions: number[] = [];

    // Generate from 0.00001 to 1000
    for (let exp = -5; exp <= 3; exp++) {
      const multiplier = 10 ** exp;
      for (const base of baseValues) {
        allPrecisions.push(base * multiplier);
      }
    }

    // Filter to valid range and ensure divisible by tick_size
    for (const precision of allPrecisions) {
      if (precision < minPrecision) continue;
      if (precision > maxPrecision) break;

      // Ensure precision is a multiple of tick_size (or close enough)
      const ratio = precision / tickSize;
      if (Math.abs(ratio - Math.round(ratio)) > 0.0001) continue;

      const formatted = this.formatPrecisionValue(precision);
      options.push({ value: formatted, label: formatted });
    }

    // Ensure we have at least 4 options
    if (options.length < 4) {
      // Fallback: generate based on price magnitude
      const magnitude = 10 ** (Math.floor(Math.log10(lastPrice)) - 2);
      const fallbackValues = [100, 50, 10, 5, 1, 0.5, 0.1].map((v) => v * magnitude);
      return fallbackValues
        .filter((v) => v >= tickSize)
        .slice(0, 7)
        .map((v) => {
          const formatted = this.formatPrecisionValue(v);
          return { value: formatted, label: formatted };
        });
    }

    // Return in descending order (largest first)
    return options.reverse();
  };

  private formatPrecisionValue = (value: number): string => {
    if (value >= 1) {
      return value.toString();
    }
    // For small numbers, find appropriate decimal places
    const str = value.toFixed(10);
    // Remove trailing zeros
    return str.replace(/\.?0+$/, "");
  };

  getInstrumentInfo = (symbol: string): InstrumentInfo | undefined => {
    return this.instrumentsInfo.get(symbol);
  };

  getLotSizeFilter = (symbol: string) => {
    const info = this.instrumentsInfo.get(symbol);
    if (!info) return null;

    return {
      minOrderQty: Number.parseFloat(info.lot_size_filter.min_order_qty),
      maxOrderQty: Number.parseFloat(info.lot_size_filter.max_order_qty),
      qtyStep: Number.parseFloat(info.lot_size_filter.qty_step),
      minNotionalValue: Number.parseFloat(info.lot_size_filter.min_notional_value),
      maxMktOrderQty: Number.parseFloat(info.lot_size_filter.max_mkt_order_qty),
      postOnlyMaxOrderQty: Number.parseFloat(info.lot_size_filter.post_only_max_order_qty),
    };
  };

  getPriceFilter = (symbol: string) => {
    const info = this.instrumentsInfo.get(symbol);
    if (!info) return null;

    return {
      minPrice: Number.parseFloat(info.price_filter.min_price),
      maxPrice: Number.parseFloat(info.price_filter.max_price),
      tickSize: Number.parseFloat(info.price_filter.tick_size),
    };
  };

  getLeverageFilter = (symbol: string) => {
    const info = this.instrumentsInfo.get(symbol);
    if (!info) return null;

    return {
      minLeverage: Number.parseFloat(info.leverage_filter.min_leverage),
      maxLeverage: Number.parseFloat(info.leverage_filter.max_leverage),
      leverageStep: Number.parseFloat(info.leverage_filter.leverage_step),
    };
  };

  getLeverageByMarketAddr = (marketAddr: string): number => {
    const leverage = this.leverages.get(marketAddr);
    const market = this.allMarkets.find((m) => m.addr === marketAddr);
    return leverage ?? Number(market?.max_leverage ?? 1);
  };

  isFavorite = (marketAddr: string): boolean => {
    return this.favoriteMarketAddresses.includes(marketAddr);
  };

  setActiveMarket = (market: Market | null) => {
    if (!market) {
      this.activeMarket = null;
      // Reset stable fields
      this._stableActiveMarketAddr = null;
      this._stableActiveMarketSymbol = null;
      this._stableActiveMarketQuoteDecimals = 0;
      this._stableActiveMarketBaseDecimals = 0;
      return;
    }

    const isMarketChanging = this.activeMarket?.addr !== market.addr;
    if (isMarketChanging) {
      this.rootStore.tradesStore.clearTrades();
      // Update stable fields only on market change
      this._stableActiveMarketAddr = market.addr;
      this._stableActiveMarketSymbol = market.symbol;
      this._stableActiveMarketQuoteDecimals = market.quote_decimals;
      this._stableActiveMarketBaseDecimals = market.base_decimals;
    }

    this.activeMarket = {
      ...market,
      base_symbol: getSymbol(market.symbol),
    };

    // Load funding rate data from REST API when market changes
    if (isMarketChanging) {
      void this.loadMarketData(market);
    }
  };

  setMarketData = (market: string, data: MarketData | null) => {
    this.marketData.set(market, data);
    this.pruneMarketData();
  };

  setLatestFillPrice = (price: BN | null) => {
    this.latestFillPrice = price;
  };

  reset = () => {
    this.marketData.clear();
    this.tickerBuffer.clear();
    this.tickerFlushScheduled = false;
  };

  private pruneMarketData = () => {
    if (this.marketData.size <= MAX_MARKET_DATA_ENTRIES) return;

    const activeAddr = this._stableActiveMarketAddr;
    const favoriteSet = new Set(this.favoriteMarketAddresses);
    const knownAddrs = new Set(this.allMarkets.map((m) => m.addr));

    for (const key of this.marketData.keys()) {
      if (this.marketData.size <= MAX_MARKET_DATA_ENTRIES) break;
      if (key === activeAddr || favoriteSet.has(key) || knownAddrs.has(key)) continue;
      this.marketData.delete(key);
    }
  };

  setLeverages = (leverages: Leverage[], clearExisting = false) => {
    if (clearExisting) {
      this.leverages.clear();
    }
    leverages.forEach((l) => {
      const marketAddr = Address.from(l.market_addr).toString();
      this.leverages.set(marketAddr, l.leverage);
    });
  };

  addPendingLeverageChange = (marketAddr: string, requestedLeverage: number) => {
    this.pendingLeverageChanges.set(marketAddr, { requestedLeverage, active: false });
  };

  activatePendingLeverageChange = (marketAddr: string) => {
    const pending = this.pendingLeverageChanges.get(marketAddr);
    if (pending) {
      this.pendingLeverageChanges.set(marketAddr, { ...pending, active: true });
    }
  };

  cancelPendingLeverageChange = (marketAddr: string) => {
    this.pendingLeverageChanges.delete(marketAddr);
  };

  /** Consumes the pending entry only if the actual leverage matches the request. */
  confirmPendingLeverageChange = (marketAddr: string, actualLeverage: number): boolean => {
    const pending = this.pendingLeverageChanges.get(marketAddr);
    if (!pending || !pending.active) return false;
    if (pending.requestedLeverage !== actualLeverage) return false;
    this.pendingLeverageChanges.delete(marketAddr);
    return true;
  };

  /** Consumes (and removes) the pending entry unconditionally. Used by the timeout. */
  consumePendingLeverageChange = (marketAddr: string): number | undefined => {
    const pending = this.pendingLeverageChanges.get(marketAddr);
    if (!pending) return undefined;
    this.pendingLeverageChanges.delete(marketAddr);
    return pending.requestedLeverage;
  };

  setRequiredMarketId = (marketId: string | null) => {
    this.requiredMarketId = marketId;
  };

  setAllMarkets = (markets: Market[]) => {
    const mappedMarkets = markets.map((market) => ({
      ...market,
      base_symbol: getSymbol(market.symbol),
      addr: Address.from(market.addr).toString(),
      quote_addr: Address.from(market.quote_addr).toString(),
    }));

    // Sort markets by predefined order
    mappedMarkets.sort((a, b) => {
      const orderA = MARKET_SORT_ORDER[a.base_symbol] ?? 999;
      const orderB = MARKET_SORT_ORDER[b.base_symbol] ?? 999;
      return orderA - orderB;
    });

    this.allMarkets = mappedMarkets;

    // Build index for fast lookup
    this.marketIndexBySymbol.clear();
    this.allMarkets.forEach((market, index) => {
      this.marketIndexBySymbol.set(market.symbol, index);
    });

    if (this.activeMarket) {
      const updatedMarket = this.allMarkets.find((m) => m.addr === this.activeMarket?.addr);
      if (updatedMarket) {
        Object.assign(this.activeMarket, updatedMarket);
      }
    }
  };

  setAllTokensMetadata = (tokens: Map<string, TokenMetadata | null>) => {
    const normalizedTokens: Record<string, TokenMetadata | null> = {};
    for (const [address, metadata] of tokens.entries()) {
      normalizedTokens[Address.from(address).toString()] = metadata;
    }
    this.allTokensMetadata = new Map(Object.entries(normalizedTokens));
  };

  toggleFavorite = (marketAddr: string) => {
    const index = this.favoriteMarketAddresses.indexOf(marketAddr);
    if (index > -1) {
      this.favoriteMarketAddresses.splice(index, 1);
    } else {
      this.favoriteMarketAddresses.push(marketAddr);
    }
    this.saveFavoritesToStorage();
  };

  loadMarkets = async (markets: Market[]) => {
    const result = await marketService.loadMarketsData(markets);

    runInAction(() => {
      this.setAllTokensMetadata(new Map(Object.entries(result.metadata)));
      this.setAllMarkets(markets);
    });
  };

  setInstrumentsInfo = (instruments: InstrumentInfo[]) => {
    this.instrumentsInfo.clear();
    for (const info of instruments) {
      this.instrumentsInfo.set(info.symbol, info);
    }
  };

  loadInstrumentsInfo = async () => {
    const instruments = await marketService.loadInstrumentsInfo();

    runInAction(() => {
      this.setInstrumentsInfo(instruments);
    });
  };

  loadMarketStats = async (market: Market) => {
    const result = await marketService.loadMarketStats(market.symbol);

    const existingData = this.marketData.get(market.addr) || {};
    this.setMarketData(market.addr, {
      ...existingData,
      volume24h: result.volume24h,
      change24h: result.change24h,
      high24h: result.high24h,
      low24h: result.low24h,
    });
  };

  loadMarketData = async (market: Market) => {
    const existing = this.marketData.get(market.addr) || {};
    const result = await marketService.loadFullMarketData(market.symbol);
    this.setMarketData(market.addr, {
      ...existing,
      ...result,
    });
  };

  loadTickers = async () => {
    const tickers = await marketService.loadTickers();

    runInAction(() => {
      for (const ticker of tickers) {
        const marketIndex = this.allMarkets.findIndex((m) => m.symbol === ticker.symbol);
        if (marketIndex !== -1) {
          const market = this.allMarkets[marketIndex];
          const fundingRatePct = String(Number(ticker.funding_rate) * 100);
          this.allMarkets[marketIndex] = {
            ...market,
            funding_rate_percentage: fundingRatePct,
            open_interest_value: ticker.open_interest_value,
          };

          const existing = this.marketData.get(market.addr) || {};
          const tickerNextFundingTime = ticker.next_funding_time;
          const nextFundingTime =
            tickerNextFundingTime && tickerNextFundingTime > Date.now()
              ? new Date(tickerNextFundingTime)
              : existing.nextFundingTime;
          this.marketData.set(market.addr, {
            ...existing,
            fundingRate: Number(ticker.funding_rate) * 100 || 0,
            ...(nextFundingTime && { nextFundingTime }),
          });
        }

        if (this.activeMarket && this.activeMarket.symbol === ticker.symbol) {
          const fundingRatePct = String(Number(ticker.funding_rate) * 100);
          this.activeMarket = {
            ...this.activeMarket,
            funding_rate_percentage: fundingRatePct,
            open_interest_value: ticker.open_interest_value,
          };
        }
      }
    });
  };

  applyTickerSnapshot = (snapshot: WSTickerSnapshot) => {
    // Buffer the snapshot - only keep the latest for each symbol
    this.tickerBuffer.set(snapshot.symbol, snapshot);

    // Schedule flush if not already scheduled
    if (!this.tickerFlushScheduled) {
      this.tickerFlushScheduled = true;
      setTimeout(() => this.flushTickerBuffer(), TICKER_THROTTLE_MS);
    }
  };

  private flushTickerBuffer = () => {
    this.tickerFlushScheduled = false;

    if (this.tickerBuffer.size === 0) return;

    const snapshots = Array.from(this.tickerBuffer.values());
    this.tickerBuffer.clear();

    runInAction(() => {
      const sanitize = (val: string) => (val === "NaN" ? "0" : val);

      for (const snapshot of snapshots) {
        const marketIndex = this.marketIndexBySymbol.get(snapshot.symbol);
        if (marketIndex !== undefined) {
          const market = this.allMarkets[marketIndex];

          const lastPrice = Number(snapshot.last_price);
          const prevPrice24h = Number(snapshot.prev_price_24h);
          const change24h =
            prevPrice24h !== 0 && !Number.isNaN(lastPrice) && !Number.isNaN(prevPrice24h)
              ? ((lastPrice - prevPrice24h) / prevPrice24h) * 100
              : 0;

          const wsSnapshot = snapshot as any;
          const rawFundingRate = wsSnapshot.funding_rate_percentage || String(Number(snapshot.funding_rate) * 100);
          const newFundingRate = sanitize(rawFundingRate);
          const fundingRateValue = newFundingRate === "0" ? market.funding_rate_percentage : newFundingRate;

          this.allMarkets[marketIndex] = {
            ...market,
            last_price: sanitize(snapshot.last_price),
            mark_price: sanitize(snapshot.mark_price),
            oracle_price: sanitize(snapshot.index_price),
            open_interest: sanitize(snapshot.open_interest),
            open_interest_value: sanitize(wsSnapshot.open_interest_value),
            funding_rate_percentage: fundingRateValue,
            volume_24h: sanitize(snapshot.volume_24h),
            turnover_24h: sanitize(snapshot.turnover_24h),
          };

          const existing = this.marketData.get(market.addr) || {};
          const wsNextFundingTime = snapshot.next_funding_time;
          const nextFundingTime =
            wsNextFundingTime && wsNextFundingTime > Date.now()
              ? new Date(wsNextFundingTime)
              : existing.nextFundingTime;
          this.marketData.set(market.addr, {
            ...existing,
            fundingRate: Number(snapshot.funding_rate) * 100 || 0,
            ...(nextFundingTime && { nextFundingTime }),
            oraclePrice: Number(snapshot.index_price) || 0,
            volume24h: Number(snapshot.turnover_24h) || 0,
            high24h: Number(snapshot.high_price_24h) || 0,
            low24h: Number(snapshot.low_price_24h) || 0,
            change24h: change24h || 0,
          });
        }

        if (this.activeMarket && this.activeMarket.symbol === snapshot.symbol) {
          const wsSnapshot = snapshot as any;
          const rawActiveFundingRate =
            wsSnapshot.funding_rate_percentage || String(Number(snapshot.funding_rate) * 100);
          const activeFundingRate = sanitize(rawActiveFundingRate);
          const activeFundingRateValue =
            activeFundingRate === "0" ? this.activeMarket.funding_rate_percentage : activeFundingRate;

          this.activeMarket = {
            ...this.activeMarket,
            last_price: sanitize(snapshot.last_price),
            mark_price: sanitize(snapshot.mark_price),
            oracle_price: sanitize(snapshot.index_price),
            open_interest: sanitize(snapshot.open_interest),
            open_interest_value: sanitize(wsSnapshot.open_interest_value),
            funding_rate_percentage: activeFundingRateValue,
            volume_24h: sanitize(snapshot.volume_24h),
            turnover_24h: sanitize(snapshot.turnover_24h),
          };
        }
      }
    });
  };

  private loadFavoritesFromStorage = () => {
    try {
      const stored = localStorage.getItem(FAVORITE_MARKETS_KEY);
      if (stored) {
        this.favoriteMarketAddresses = JSON.parse(stored);
      }
    } catch {}
  };

  private saveFavoritesToStorage = () => {
    try {
      localStorage.setItem(FAVORITE_MARKETS_KEY, JSON.stringify(this.favoriteMarketAddresses));
    } catch {}
  };
}
